import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import useCRM from '../../hooks/useCRM';
import useApprenants from '../../hooks/useApprenants';

const CRMModule = () => {
  const [activePage, setActivePage] = useState('pipeline');
  const [activeProspectFilter, setActiveProspectFilter] = useState('tous');
  const [showNewOppModal, setShowNewOppModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNewProspectModal, setShowNewProspectModal] = useState(false);
  const [showNewDevisModal, setShowNewDevisModal] = useState(false);
  const [showNewRelanceModal, setShowNewRelanceModal] = useState(false);
  const [showNewActiviteModal, setShowNewActiviteModal] = useState(false);
  const [showNewApprenantModal, setShowNewApprenantModal] = useState(false);
  const [editingApprenant, setEditingApprenant] = useState(null);
  const [searchApprenant, setSearchApprenant] = useState(''); // 🔍 État pour la recherche
  const initialNewApprenantState = {
    prenom: '',
    nom: '',
    formation: '',
    debut: new Date().toISOString().split('T')[0],
    duree: '',
    statut: 'en_cours',
    filiale_id: '',
    responsable_id: '',
    commentaire: '',
    montant_a_payer: '',
    montant_paye: '',
    reste_a_payer: '',
    statut_paiement: 'non_paye'
  };
  const [newApprenant, setNewApprenant] = useState(initialNewApprenantState);

  const { apprenants, loading: apprenantsLoading, error: apprenantsError, createApprenant, updateApprenant, deleteApprenant, loadApprenants } = useApprenants({ autoLoad: true });
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filiales, setFiliales] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Hook CRM pour gérer les données de la base
  const crm = useCRM({ autoLoad: true });
  
  // 🔧 Afficher les erreurs de chargement CRM
  useEffect(() => {
    if (crm.error) {
      console.error('❌ Erreur CRM:', crm.error);
    }
  }, [crm.loading, crm.error]);
  
  const [newOpp, setNewOpp] = useState({
    name: '',
    client: '',
    description: '',
    status: 'negociation',
    priority: 'normale',
    budget: '',
    start_date: new Date().toISOString().split('T')[0],
    filiale_id: '',
    departement_id: '',
  });

  const [newProspect, setNewProspect] = useState({
    nom_entreprise: '',
    secteur_activite: '',
    service_interesse: '',
    contact_nom: '',
    contact_tel: '',
    contact_email: '',
    valeur_potentielle: '',
    temperature: 'tiede',
    responsable_id: ''
  });

  const [newDevis, setNewDevis] = useState({
    client_nom: '',
    service: '',
    objet: '',
    montant: '',
    date_validite: '',
    responsable_id: '',
    prospect_id: ''
  });

  const [newRelance, setNewRelance] = useState({
    contact_nom: '',
    objet: '',
    type: 'prospect',
    date_prevue: '',
    canal: 'email',
    priorite: 'normale',
    responsable_id: '',
    prospect_id: '',
    devis_id: ''
  });

  const [newActivite, setNewActivite] = useState({
    contact_nom: '',
    objet: '',
    type: 'appel',
    date_activite: new Date().toISOString().split('T')[0] + 'T10:00',
    resultat: '',
    action_suivante: '',
    responsable_id: '',
    prospect_id: '',
    devis_id: '',
    project_id: ''
  });

  // Charger les données au montage
  useEffect(() => {
    loadProjects();
    loadFiliales();
    loadDepartements();
    loadEmployees();
    loadApprenants();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await api.projects.getAll();
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Erreur chargement projets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFiliales = async () => {
    try {
      const response = await api.filiales.getAll();
      setFiliales(Array.isArray(response) ? response : (response?.filiales || response?.data || []));
    } catch (error) {
      console.error('Erreur chargement filiales:', error);
    }
  };

  const loadDepartements = async () => {
    try {
      const response = await api.departements.getAll();
      setDepartements(Array.isArray(response) ? response : (response?.departements || response?.data || []));
    } catch (error) {
      console.error('Erreur chargement départements:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.employees.getAll();
      setEmployees(Array.isArray(response) ? response : (response?.employees || response?.data || []));
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    }
  };

  const handleCreateOpp = async () => {
    try {
      if (!newOpp.name || !newOpp.client || !newOpp.filiale_id) {
        alert('Veuillez remplir tous les champs obligatoires');
        return;
      }

      setLoading(true);
      await api.projects.create({
        ...newOpp,
        budget: parseFloat(newOpp.budget) || 0,
        progress: 0,
        depenses_reelles: 0,
      });

      alert('✅ Opportunité créée avec succès !');
      setShowNewOppModal(false);
      setNewOpp({
        name: '',
        client: '',
        description: '',
        status: 'negociation',
        priority: 'normale',
        budget: '',
        start_date: new Date().toISOString().split('T')[0],
        filiale_id: '',
        departement_id: '',
      });
      loadProjects();
    } catch (error) {
      console.error('Erreur création opportunité:', error);
      alert('❌ Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleOppClick = async (opp) => {
    setSelectedOpp(opp);
    
    // Si c'est un projet réel, charger les détails complets depuis l'API
    if (opp.id && opp.id.toString().startsWith('proj-')) {
      try {
        const projectId = opp.id.toString().replace('proj-', '');
        const response = await api.projects.getById(projectId);
        setSelectedOpp({
          ...opp,
          fullDetails: response.project
        });
      } catch (error) {
        console.error('Erreur chargement détails:', error);
      }
    }
    
    setShowDetailModal(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      'planification': '📋 Planification',
      'negociation': '🤝 Négociation',
      'en_cours': '🚀 En cours',
      'production': '⚙️ Production',
      'termine': '✅ Terminé',
      'annule': '❌ Annulé'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'basse': '🟢 Basse',
      'normale': '🟡 Normale',
      'haute': '🟠 Haute',
      'urgente': '🔴 Urgente'
    };
    return labels[priority] || priority;
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('fr-FR');
    }
    return String(value).split('T')[0];
  };

  const formatCurrency = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '0';
    return amount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleCreateProspect = async () => {
    if (!newProspect.nom_entreprise || !newProspect.secteur_activite) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    const result = await crm.createProspect(newProspect);
    setLoading(false);

    if (result.success) {
      alert('✅ Prospect créé avec succès !');
      setShowNewProspectModal(false);
      setNewProspect({
        nom_entreprise: '',
        secteur_activite: '',
        service_interesse: '',
        contact_nom: '',
        contact_tel: '',
        contact_email: '',
        valeur_potentielle: '',
        temperature: 'tiede',
        responsable_id: ''
      });
    } else {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  const handleCreateDevis = async () => {
    if (!newDevis.client_nom || !newDevis.objet || !newDevis.montant || !newDevis.date_validite) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    const result = await crm.createDevis(newDevis);
    setLoading(false);

    if (result.success) {
      alert(`✅ Devis ${result.devis.numero} créé avec succès !`);
      setShowNewDevisModal(false);
      setNewDevis({
        client_nom: '',
        service: '',
        objet: '',
        montant: '',
        date_validite: '',
        responsable_id: '',
        prospect_id: ''
      });
    } else {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  const handleCreateRelance = async () => {
    if (!newRelance.contact_nom || !newRelance.objet || !newRelance.date_prevue) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    const result = await crm.createRelance(newRelance);
    setLoading(false);

    if (result.success) {
      alert('✅ Relance planifiée avec succès !');
      setShowNewRelanceModal(false);
      setNewRelance({
        contact_nom: '',
        objet: '',
        type: 'prospect',
        date_prevue: '',
        canal: 'email',
        priorite: 'normale',
        responsable_id: '',
        prospect_id: '',
        devis_id: ''
      });
    } else {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  const handleCreateActivite = async () => {
    if (!newActivite.contact_nom || !newActivite.objet || !newActivite.date_activite) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    const result = await crm.createActivite(newActivite);
    setLoading(false);

    if (result.success) {
      alert('✅ Activité enregistrée avec succès !');
      setShowNewActiviteModal(false);
      setNewActivite({
        contact_nom: '',
        objet: '',
        type: 'appel',
        date_activite: new Date().toISOString().split('T')[0] + 'T10:00',
        resultat: '',
        action_suivante: '',
        responsable_id: '',
        prospect_id: '',
        devis_id: '',
        project_id: ''
      });
    } else {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  const openApprenantModal = (apprenant = null) => {
    if (apprenant) {
      setEditingApprenant(apprenant);
      setNewApprenant({
        prenom: apprenant.prenom || '',
        nom: apprenant.nom || '',
        formation: apprenant.formation || '',
        debut: apprenant.debut || new Date().toISOString().split('T')[0],
        duree: apprenant.duree || '',
        statut: apprenant.statut || 'en_cours',
        filiale_id: apprenant.filiale_id || '',
        responsable_id: apprenant.responsable_id || apprenant.responsable?.id || '',
        commentaire: apprenant.commentaire || '',
        montant_a_payer: apprenant.montant_a_payer || '',
        montant_paye: apprenant.montant_paye || '',
        reste_a_payer: apprenant.reste_a_payer || '',
        statut_paiement: apprenant.statut_paiement || 'non_paye'
      });
    } else {
      setEditingApprenant(null);
      setNewApprenant(initialNewApprenantState);
    }
    setShowNewApprenantModal(true);
  };

  const updateMontantField = (field, value) => {
    setNewApprenant(prev => {
      const next = { ...prev, [field]: value };
      const montant = parseFloat(field === 'montant_a_payer' ? value : prev.montant_a_payer) || 0;
      const paye = parseFloat(field === 'montant_paye' ? value : prev.montant_paye) || 0;
      next.reste_a_payer = String(Math.max(0, montant - paye));
      return next;
    });
  };

  const handleSaveApprenant = async () => {
    if (!newApprenant.nom || !newApprenant.prenom || !newApprenant.formation || !newApprenant.debut) {
      alert('Veuillez renseigner le prénom, nom, formation et date de début');
      return;
    }

    // 🔧 VALIDATION : Vérifier si un apprenant avec le même nom et prénom existe déjà
    const nomComplet = `${newApprenant.prenom.trim().toLowerCase()} ${newApprenant.nom.trim().toLowerCase()}`;
    const doublon = apprenants.find(a => {
      // Ignorer l'apprenant en cours d'édition
      if (editingApprenant?.id && a.id === editingApprenant.id) return false;
      const nomExistant = `${a.prenom.trim().toLowerCase()} ${a.nom.trim().toLowerCase()}`;
      return nomExistant === nomComplet;
    });

    if (doublon) {
      alert(`❌ Un apprenant avec le nom "${newApprenant.prenom} ${newApprenant.nom}" existe déjà dans la liste !`);
      return;
    }

    setLoading(true);
    const result = editingApprenant?.id
      ? await updateApprenant(editingApprenant.id, newApprenant)
      : await createApprenant(newApprenant);
    setLoading(false);

    if (result.success) {
      alert('✅ Apprenant enregistré avec succès !');
      setShowNewApprenantModal(false);
      setEditingApprenant(null);
      setNewApprenant(initialNewApprenantState);
    } else {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  const handleDeleteApprenant = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet apprenant ?')) return;

    setLoading(true);
    const result = await deleteApprenant(id);
    setLoading(false);

    if (!result.success) {
      alert(`❌ Erreur : ${result.error}`);
    }
  };

  // Combiner les opportunités statiques avec les projets réels de la base
  const OPPORTUNITES = [
    ...projects.map(p => ({
      id: `proj-${p.id}`,
      nom: p.name,
      client: p.client || 'N/A',
      service: p.filiale?.nom || 'N/A',
      montant: parseFloat(p.budget) || 0,
      prob: p.progress || 0,
      etape: p.status === 'negociation' ? 'negociation' : p.status === 'en_cours' ? 'proposition' : p.status === 'termine' ? 'accord' : 'qualif',
      resp: p.owner?.name || 'N/A',
      cloture: p.end_date || ''
    }))
  ];

  // Utiliser les données du hook CRM au lieu de données statiques
  const PROSPECTS = crm.prospects.map(p => ({
    id: p.id,
    nom: p.nom_entreprise,
    secteur: p.secteur_activite || 'N/A',
    service: p.service_interesse || 'N/A',
    contact: p.contact_nom || 'N/A',
    tel: p.contact_tel || 'N/A',
    potentiel: parseFloat(p.valeur_potentielle) || 0,
    temp: p.temperature,
    derniere: p.derniere_activite || '-',
    prochaine: p.prochaine_activite || '-',
    resp: p.responsable?.name || 'Non assigné'
  }));

  const DEVIS = crm.devis.map(d => ({
    id: d.id,
    num: d.numero,
    client: d.client_nom,
    service: d.service || 'N/A',
    objet: d.objet,
    montant: parseFloat(d.montant),
    date: d.date_emission,
    validite: d.date_validite,
    statut: d.statut,
    resp: d.responsable?.name || 'Non assigné'
  }));

  const RELANCES = crm.relances.map(r => ({
    id: r.id,
    contact: r.contact_nom,
    objet: r.objet,
    type: r.type,
    date: r.date_prevue,
    canal: r.canal || 'N/A',
    resultat: r.resultat || '-',
    resp: r.responsable?.name || 'Non assigné',
    urgent: r.priorite === 'urgente' || r.priorite === 'haute'
  }));

  const ACTIVITES = crm.activites.map(a => ({
    id: a.id,
    date: a.date_activite,
    type: a.type,
    contact: a.contact_nom,
    objet: a.objet,
    resultat: a.resultat || '-',
    prochaine: a.action_suivante || '-',
    resp: a.responsable?.name || 'Non assigné'
  }));

  const fmtN = n => Math.round(n).toLocaleString('fr-FR');
  const fmtK = n => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'k' : n;

  const totalOpp = OPPORTUNITES.length;
  const valeurPipe = OPPORTUNITES.reduce((s, o) => s + o.montant, 0);
  const gagnes = OPPORTUNITES.filter(o => o.etape === 'accord').length;
  const tauxConv = totalOpp > 0 ? Math.round((gagnes / totalOpp) * 100) : 0;

  const totalDevis = DEVIS.length;
  const devisAttente = DEVIS.filter(d => d.statut === 'attente' || d.statut === 'envoye').length;
  const devisAcceptes = DEVIS.filter(d => d.statut === 'accepte').length;
  const montantAttente = DEVIS.filter(d => d.statut === 'attente' || d.statut === 'envoye').reduce((s, d) => s + d.montant, 0);

  const relancesUrgentes = RELANCES.filter(r => r.urgent);

  const filteredProspects = activeProspectFilter === 'tous' ? PROSPECTS : PROSPECTS.filter(p => p.temp === activeProspectFilter);

  const renderPipeline = () => {
    const etapes = [
      { id: 'contact', nom: 'Prise de contact', color: '#42a5f5', opps: OPPORTUNITES.filter(o => o.etape === 'contact') },
      { id: 'qualif', nom: 'Qualification', color: '#ab47bc', opps: OPPORTUNITES.filter(o => o.etape === 'qualif') },
      { id: 'proposition', nom: 'Proposition', color: '#ff9800', opps: OPPORTUNITES.filter(o => o.etape === 'proposition') },
      { id: 'negociation', nom: 'Negociation', color: '#C9A84C', opps: OPPORTUNITES.filter(o => o.etape === 'negociation') },
      { id: 'accord', nom: 'Accord verbal', color: '#66bb6a', opps: OPPORTUNITES.filter(o => o.etape === 'accord') }
    ];

    return (
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 10 }}>
        {etapes.map(etape => (
          <div key={etape.id} style={{ minWidth: 240, flexShrink: 0 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '10px 10px 0 0',
              background: `${etape.color}22`,
              marginBottom: 8
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: etape.color }}>
                {etape.nom}
              </div>
              <div style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 99,
                background: 'rgba(255,255,255,0.1)',
                color: '#fff'
              }}>
                {etape.opps.length}
              </div>
            </div>
            {etape.opps.map(opp => (
              <div
                key={opp.id}
                onClick={() => handleOppClick(opp)}
                style={{
                  background: '#162535',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 10,
                  padding: '14px 15px',
                  marginBottom: 10,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 5 }}>{opp.nom}</div>
                <div style={{ fontSize: 11, color: '#667', marginBottom: 10 }}>{opp.client}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, fontWeight: 600, color: '#C9A84C' }}>
                    {fmtK(opp.montant)} F
                  </div>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 99,
                    background: 'rgba(255,255,255,0.06)',
                    color: '#888'
                  }}>
                    {opp.prob}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ background: '#0d1b2a', minHeight: '100vh', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: '#fff' }}>
      <div style={{ padding: '24px' }}>
        
        {/* 🔧 INDICATEUR DE CHARGEMENT */}
        {crm.loading && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            background: 'rgba(201,168,76,0.1)', 
            border: '1px solid rgba(201,168,76,0.3)', 
            borderRadius: 10,
            marginBottom: 20,
            color: '#C9A84C'
          }}>
            ⏳ Chargement des données CRM...
          </div>
        )}

        {/* 🔧 INDICATEUR D'ERREUR */}
        {crm.error && !crm.loading && (
          <div style={{ 
            padding: '16px 20px', 
            background: 'rgba(239,83,80,0.1)', 
            border: '1px solid rgba(239,83,80,0.3)', 
            borderRadius: 10,
            marginBottom: 20,
            color: '#ef8080',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Erreur de chargement des données CRM</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{crm.error}</div>
              <button 
                onClick={() => crm.reloadAll()} 
                style={{
                  marginTop: 8,
                  background: 'rgba(239,83,80,0.2)',
                  border: '1px solid rgba(239,83,80,0.4)',
                  borderRadius: 6,
                  padding: '6px 12px',
                  color: '#ef8080',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                🔄 Réessayer
              </button>
            </div>
          </div>
        )}
        
        {/* TABS */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: 24, overflowX: 'auto' }}>
          {[
            { id: 'pipeline', label: 'Pipeline', badge: totalOpp, badgeColor: '#64b5f6' },
            { id: 'prospects', label: 'Prospects', badge: PROSPECTS.length, badgeColor: '#ffb74d' },
            { id: 'devis', label: 'Devis', badge: totalDevis, badgeColor: '#C9A84C' },
            { id: 'relances', label: 'Relances', badge: relancesUrgentes.length, badgeColor: '#ef8080' },
            { id: 'activites', label: 'Activites', badge: null },
            { id: 'apprenants', label: 'Apprenants', badge: apprenants.length, badgeColor: '#ab47bc' },
            { id: 'stats', label: 'Statistiques', badge: null }
          ].map(tab => (
            <div
              key={tab.id}
              onClick={() => setActivePage(tab.id)}
              style={{
                padding: '12px 18px',
                fontSize: 12.5,
                color: activePage === tab.id ? '#C9A84C' : '#667',
                cursor: 'pointer',
                borderBottom: activePage === tab.id ? '2px solid #C9A84C' : '2px solid transparent',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {tab.label}
              {tab.badge !== null && (
                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 99,
                  background: `${tab.badgeColor}33`,
                  color: tab.badgeColor
                }}>
                  {tab.badge}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* PIPELINE PAGE */}
        {activePage === 'pipeline' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Pipeline Commercial
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Vision temps reel - Toutes filiales FHC Groupe SARL
                </div>
              </div>
              <button 
                onClick={() => setShowNewOppModal(true)}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                + Nouvelle opportunite
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#42a5f5' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>🎯</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{totalOpp}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Opportunites actives</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#C9A84C' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>💰</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{fmtK(valeurPipe)}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Valeur pipeline</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#66bb6a' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>✅</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{gagnes}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Accords verbaux</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ff9800' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>📊</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{tauxConv}%</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Taux de conversion</div>
              </div>
            </div>

            {renderPipeline()}
          </div>
        )}

        {/* PROSPECTS PAGE */}
        {activePage === 'prospects' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Gestion des Prospects
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Tous les contacts commerciaux
                </div>
              </div>
              <button 
                onClick={() => setShowNewProspectModal(true)}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                + Nouveau prospect
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { id: 'tous', label: 'Tous' },
                { id: 'chaud', label: '🔥 Chauds' },
                { id: 'tiede', label: '🟡 Tiedes' },
                { id: 'froid', label: '🔵 Froids' }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setActiveProspectFilter(filter.id)}
                  style={{
                    fontSize: 11,
                    padding: '6px 14px',
                    borderRadius: 99,
                    border: '1.5px solid rgba(255,255,255,0.07)',
                    background: activeProspectFilter === filter.id ? 'rgba(201,168,76,0.12)' : 'transparent',
                    color: activeProspectFilter === filter.id ? '#C9A84C' : '#667',
                    cursor: 'pointer',
                    fontWeight: activeProspectFilter === filter.id ? 600 : 400
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        🏢 Entreprise
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        🏭 Secteur d'activité
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        🎯 Filiale intéressée
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        👤 Personne de contact
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        💰 Valeur potentielle
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        🌡️ Niveau d'intérêt
                      </span>
                    </th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        👨‍💼 Chargé de compte
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProspects.map((p, i) => (
                    <tr
                      key={p.id}
                      style={{
                        borderBottom: i < filteredProspects.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ fontSize: 12, color: '#fff', padding: '12px 12px', fontWeight: 500 }}>{p.nom}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{p.secteur}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{p.service}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{p.contact}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{fmtN(p.potentiel)} F</td>
                      <td style={{ fontSize: 11, padding: '12px 12px' }}>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '3px 9px',
                          borderRadius: 99,
                          background: p.temp === 'chaud' ? 'rgba(229,57,53,0.15)' : p.temp === 'tiede' ? 'rgba(251,140,0,0.15)' : 'rgba(25,118,210,0.15)',
                          color: p.temp === 'chaud' ? '#ef8080' : p.temp === 'tiede' ? '#ffb74d' : '#64b5f6'
                        }}>
                          {p.temp === 'chaud' ? '🔥 Chaud' : p.temp === 'tiede' ? '🟡 Tiede' : '🔵 Froid'}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{p.resp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* DEVIS PAGE */}
        {activePage === 'devis' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Devis & Offres Commerciales
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Propositions en cours - Suivi acceptation
                </div>
              </div>
              <button 
                onClick={() => setShowNewDevisModal(true)}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                + Nouveau devis
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#42a5f5' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>📋</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{totalDevis}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Devis total</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ff9800' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>⏳</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{devisAttente}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>En attente</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#66bb6a' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>✅</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{devisAcceptes}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Acceptes</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#C9A84C' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>💰</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{fmtK(montantAttente)}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Montant en attente</div>
              </div>
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>N° Devis</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Client</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Service</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Objet</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Montant HT</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Date</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Validite</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Statut</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {DEVIS.map((d, i) => (
                    <tr
                      key={d.id}
                      style={{
                        borderBottom: i < DEVIS.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.num}</td>
                      <td style={{ fontSize: 12, color: '#fff', padding: '12px 12px', fontWeight: 500 }}>{d.client}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.service}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.objet}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{fmtN(d.montant)} F</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.date}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.validite}</td>
                      <td style={{ fontSize: 11, padding: '12px 12px' }}>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '3px 9px',
                          borderRadius: 99,
                          background: d.statut === 'accepte' ? 'rgba(76,175,80,0.15)' : d.statut === 'attente' ? 'rgba(251,140,0,0.15)' : 'rgba(239,83,80,0.15)',
                          color: d.statut === 'accepte' ? '#66bb6a' : d.statut === 'attente' ? '#ffb74d' : '#ef8080'
                        }}>
                          {d.statut === 'accepte' ? '✅ Accepte' : d.statut === 'attente' ? '⏳ En attente' : '❌ Refuse'}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{d.resp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* RELANCES PAGE */}
        {activePage === 'relances' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Suivi des Relances
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Devis sans reponse - Factures impayees - Prospects inactifs
                </div>
              </div>
              <button 
                onClick={() => setShowNewRelanceModal(true)}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}>
                + Planifier une relance
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🔴 Relances urgentes</span>
                  <span style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.4)',
                    background: 'rgba(255,255,255,0.05)',
                    padding: '3px 8px',
                    borderRadius: 99
                  }}>
                    {relancesUrgentes.length}
                  </span>
                </div>
                {relancesUrgentes.map(r => (
                  <div
                    key={r.id}
                    style={{
                      padding: '12px',
                      background: 'rgba(239,83,80,0.08)',
                      border: '1px solid rgba(239,83,80,0.2)',
                      borderRadius: 8,
                      marginBottom: 8,
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{r.contact}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>{r.objet}</div>
                    <div style={{ fontSize: 10, color: '#667' }}>
                      <span style={{ marginRight: 10 }}>📅 {r.date}</span>
                      <span>{r.canal}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 14 }}>
                  🟠 Relances planifiees
                </div>
                {RELANCES.filter(r => !r.urgent).map(r => (
                  <div
                    key={r.id}
                    style={{
                      padding: '12px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 8,
                      marginBottom: 8,
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{r.contact}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>{r.objet}</div>
                    <div style={{ fontSize: 10, color: '#667' }}>
                      <span style={{ marginRight: 10 }}>📅 {r.date}</span>
                      <span>{r.canal}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 14 }}>
                📅 Historique des relances
              </div>
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Contact</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Objet</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Type</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Date</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Canal</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Responsable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RELANCES.map((r, i) => (
                      <tr key={r.id} style={{ borderBottom: i < RELANCES.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.contact}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.objet}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.type}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.date}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.canal}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{r.resp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVITES PAGE */}
        {activePage === 'activites' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Journal des Activites
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Appels - Reunions - Emails - Visites
                </div>
              </div>
              <button 
                onClick={() => setShowNewActiviteModal(true)}
                style={{
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}>
                + Enregistrer une activite
              </button>
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Date</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Type</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Contact</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Objet</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Resultat</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Prochaine etape</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Resp.</th>
                  </tr>
                </thead>
                <tbody>
                  {ACTIVITES.map((a, i) => (
                    <tr
                      key={a.id}
                      style={{
                        borderBottom: i < ACTIVITES.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.date}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.type}</td>
                      <td style={{ fontSize: 12, color: '#fff', padding: '12px 12px', fontWeight: 500 }}>{a.contact}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.objet}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.resultat}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.prochaine}</td>
                      <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{a.resp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* APPRENANTS PAGE */}
        {activePage === 'apprenants' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                  Apprenants en formation
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Suivi des apprenants en cours de formation
                </div>
              </div>
              <button 
                onClick={() => openApprenantModal()}
                style={{
                  background: '#ab47bc',
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 18px',
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                + Ajouter un apprenant
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Total apprenants', value: apprenants.length, color: '#ab47bc' },
                { label: 'En cours', value: apprenants.filter(a => a.statut === 'en_cours').length, color: '#42a5f5' },
                { label: 'Terminée', value: apprenants.filter(a => a.statut === 'termine').length, color: '#66bb6a' }
              ].map(k => (
                <div key={k.label} style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>{k.label}</div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* 🔍 CHAMP DE RECHERCHE */}
            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="🔍 Rechercher un apprenant par nom ou prénom..."
                value={searchApprenant}
                onChange={(e) => setSearchApprenant(e.target.value)}
                style={{
                  width: '100%',
                  background: '#162535',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Nom</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Formation</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Début</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Durée</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Statut</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Paiement</th>
                    <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // 🔧 FILTRAGE : Recherche par nom/prénom
                    const apprenantsFiltered = apprenants.filter(a => {
                      if (!searchApprenant.trim()) return true;
                      const search = searchApprenant.toLowerCase();
                      const nomComplet = `${a.prenom} ${a.nom}`.toLowerCase();
                      return nomComplet.includes(search);
                    });

                    // 🔧 TRI ALPHABÉTIQUE : Par nom de famille puis prénom
                    const apprenantsTriés = [...apprenantsFiltered].sort((a, b) => {
                      const nomA = `${a.nom} ${a.prenom}`.toLowerCase();
                      const nomB = `${b.nom} ${b.prenom}`.toLowerCase();
                      return nomA.localeCompare(nomB, 'fr');
                    });

                    if (apprenantsTriés.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} style={{ padding: '20px 12px', textAlign: 'center', color: '#667' }}>
                            {searchApprenant.trim() 
                              ? `Aucun apprenant trouvé pour "${searchApprenant}"`
                              : 'Aucun apprenant ajouté pour l\'instant.'
                            }
                          </td>
                        </tr>
                      );
                    }

                    return apprenantsTriés.map((apprenant, idx) => (
                      <tr key={apprenant.id} style={{ borderBottom: idx < apprenantsTriés.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                        <td style={{ fontSize: 12, color: '#fff', padding: '12px 12px', fontWeight: 500 }}>{apprenant.nom} {apprenant.prenom}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{apprenant.formation}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{formatDate(apprenant.debut)}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{apprenant.duree || 'N/A'}</td>
                        <td style={{ fontSize: 11, padding: '12px 12px' }}>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '3px 9px',
                            borderRadius: 99,
                            background: apprenant.statut === 'termine' ? 'rgba(102,187,106,0.15)' : 'rgba(66,165,245,0.15)',
                            color: apprenant.statut === 'termine' ? '#66bb6a' : '#42a5f5'
                          }}>
                            {apprenant.statut === 'en_cours' ? 'En cours' : 'Terminée'}
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px', whiteSpace: 'nowrap' }}>
                          <div>À payer : {formatCurrency(apprenant.montant_a_payer)}</div>
                          <div>Payé : {formatCurrency(apprenant.montant_paye)}</div>
                          <div>Reste : {formatCurrency(apprenant.reste_a_payer)}</div>
                          <div style={{ color: apprenant.statut_paiement === 'paye' ? '#66bb6a' : apprenant.statut_paiement === 'partiel' ? '#fbc02d' : '#ef5350' }}>
                            {apprenant.statut_paiement === 'paye' ? 'Payé' : apprenant.statut_paiement === 'partiel' ? 'Partiel' : 'Non payé'}
                          </div>
                        </td>
                        <td style={{ display: 'flex', gap: 8, padding: '12px 12px' }}>
                          <button
                            onClick={() => openApprenantModal(apprenant)}
                            style={{
                              background: 'transparent',
                              border: '1px solid rgba(255,255,255,0.15)',
                              borderRadius: 8,
                              padding: '6px 10px',
                              color: '#fff',
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            ✏️ 
                          </button>
                          <button
                            onClick={() => handleDeleteApprenant(apprenant.id)}
                            style={{
                              background: '#ef5350',
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 10px',
                              color: '#1a1a0d',
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STATS PAGE */}
        {activePage === 'stats' && (
          <div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 600, lineHeight: 1.1 }}>
                Statistiques Commerciales
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                Performance - Conversion - CA previsionnel
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#66bb6a' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>💰</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>6,8M F</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>CA realise 2026</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#C9A84C' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>🎯</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{fmtK(valeurPipe)}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>CA previsionnel</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#42a5f5' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>📊</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>{tauxConv}%</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Taux conversion</div>
              </div>
              <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ff9800' }} />
                <div style={{ fontSize: 20, marginBottom: 8 }}>⏱️</div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, fontWeight: 600, lineHeight: 1, marginBottom: 4 }}>32j</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Cycle vente moyen</div>
              </div>
            </div>

            <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 14 }}>
                🏆 Top opportunites par valeur
              </div>
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Opportunite</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Service</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Montant</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Probabilite</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Etape</th>
                      <th style={{ fontSize: 10, color: '#445', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' }}>Resp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...OPPORTUNITES].sort((a, b) => b.montant - a.montant).map((o, i) => (
                      <tr key={o.id} style={{ borderBottom: i < OPPORTUNITES.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                        <td style={{ fontSize: 12, color: '#fff', padding: '12px 12px', fontWeight: 500 }}>{o.nom}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{o.service}</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{fmtN(o.montant)} F</td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{o.prob}%</td>
                        <td style={{ fontSize: 11, padding: '12px 12px' }}>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '3px 9px',
                            borderRadius: 99,
                            background: o.etape === 'accord' ? 'rgba(76,175,80,0.15)' : 'rgba(66,165,245,0.15)',
                            color: o.etape === 'accord' ? '#66bb6a' : '#64b5f6'
                          }}>
                            {o.etape}
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: '#aaa', padding: '12px 12px' }}>{o.resp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* MODAL DÉTAIL OPPORTUNITÉ */}
      {showDetailModal && selectedOpp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20
        }}
        onClick={() => setShowDetailModal(false)}
        >
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            maxWidth: 700,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              padding: '24px',
              borderBottom: '1px solid rgba(255,255,255,0.07)'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 24, fontWeight: 600, color: '#fff', marginBottom: 8 }}>
                  {selectedOpp.nom}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: 99,
                    background: 'rgba(201,168,76,0.15)',
                    color: '#C9A84C'
                  }}>
                    {selectedOpp.etape}
                  </span>
                  {selectedOpp.fullDetails && (
                    <>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 99,
                        background: 'rgba(66,165,245,0.15)',
                        color: '#64b5f6'
                      }}>
                        {getStatusLabel(selectedOpp.fullDetails.status)}
                      </span>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: 99,
                        background: 'rgba(255,152,0,0.15)',
                        color: '#ffb74d'
                      }}>
                        {getPriorityLabel(selectedOpp.fullDetails.priority)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#667',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4,
                  marginLeft: 16
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              
              {/* Informations principales */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 24 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                    Client
                  </div>
                  <div style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>
                    🏢 {selectedOpp.client}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                    Service
                  </div>
                  <div style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>
                    {selectedOpp.service}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                    Montant
                  </div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: '#C9A84C', fontWeight: 600 }}>
                    {fmtN(selectedOpp.montant)} FCFA
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                    Probabilité / Progression
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 16, color: '#fff', fontWeight: 600 }}>
                      {selectedOpp.prob}%
                    </div>
                    <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{
                        width: `${selectedOpp.prob}%`,
                        height: '100%',
                        background: selectedOpp.prob >= 75 ? '#66bb6a' : selectedOpp.prob >= 50 ? '#C9A84C' : '#64b5f6',
                        borderRadius: 99,
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                    Responsable
                  </div>
                  <div style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>
                    👤 {selectedOpp.resp}
                  </div>
                </div>

                {selectedOpp.cloture && (
                  <div>
                    <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>
                      Date de clôture prévue
                    </div>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 500 }}>
                      📅 {selectedOpp.cloture}
                    </div>
                  </div>
                )}
              </div>

              {/* Description (si projet réel) */}
              {selectedOpp.fullDetails?.description && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 10, color: '#667', fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
                    Description
                  </div>
                  <div style={{
                    fontSize: 13,
                    color: '#aaa',
                    lineHeight: 1.6,
                    background: 'rgba(255,255,255,0.03)',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    {selectedOpp.fullDetails.description}
                  </div>
                </div>
              )}

              {/* Informations financières détaillées (si projet réel) */}
              {selectedOpp.fullDetails && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 12 }}>
                    💰 Informations Financières
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.03)',
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <div style={{ fontSize: 10, color: '#667', marginBottom: 4 }}>Budget</div>
                      <div style={{ fontSize: 14, color: '#C9A84C', fontWeight: 600 }}>
                        {fmtN(selectedOpp.fullDetails.budget)} F
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255,255,255,0.03)',
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <div style={{ fontSize: 10, color: '#667', marginBottom: 4 }}>Dépensé</div>
                      <div style={{ fontSize: 14, color: '#ef8080', fontWeight: 600 }}>
                        {fmtN(selectedOpp.fullDetails.depenses_reelles)} F
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255,255,255,0.03)',
                      padding: '12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <div style={{ fontSize: 10, color: '#667', marginBottom: 4 }}>Restant</div>
                      <div style={{ fontSize: 14, color: '#66bb6a', fontWeight: 600 }}>
                        {fmtN(selectedOpp.fullDetails.budget - selectedOpp.fullDetails.depenses_reelles)} F
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Équipe (si projet réel) */}
              {selectedOpp.fullDetails?.team && selectedOpp.fullDetails.team.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 12 }}>
                    👥 Équipe Projet ({selectedOpp.fullDetails.team.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectedOpp.fullDetails.team.map((member, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(255,255,255,0.05)',
                        padding: '6px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                        color: '#aaa',
                        border: '1px solid rgba(255,255,255,0.07)'
                      }}>
                        👤 {member.user?.name || member.prenom + ' ' + member.nom || 'N/A'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tâches (si projet réel) */}
              {selectedOpp.fullDetails?.tasks && selectedOpp.fullDetails.tasks.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 12 }}>
                    ✓ Tâches ({selectedOpp.fullDetails.tasks.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedOpp.fullDetails.tasks.slice(0, 5).map((task, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(255,255,255,0.03)',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                          <span style={{ fontSize: 14 }}>
                            {task.done ? '✅' : '⏳'}
                          </span>
                          <span style={{
                            fontSize: 12,
                            color: task.done ? '#888' : '#fff',
                            textDecoration: task.done ? 'line-through' : 'none'
                          }}>
                            {task.titre}
                          </span>
                        </div>
                        {task.priority === 'urgente' && (
                          <span style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 99,
                            background: 'rgba(239,83,80,0.15)',
                            color: '#ef8080'
                          }}>
                            🔴 Urgent
                          </span>
                        )}
                      </div>
                    ))}
                    {selectedOpp.fullDetails.tasks.length > 5 && (
                      <div style={{ fontSize: 11, color: '#667', textAlign: 'center', marginTop: 4 }}>
                        ... et {selectedOpp.fullDetails.tasks.length - 5} autre(s) tâche(s)
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Footer avec actions */}
            <div style={{
              display: 'flex',
              gap: 10,
              padding: '16px 24px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.02)'
            }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#888',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Fermer
              </button>
              {selectedOpp.id && selectedOpp.id.toString().startsWith('proj-') && (
                <button
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: '#C9A84C',
                    border: 'none',
                    borderRadius: 8,
                    color: '#1a1a0d',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    alert('Fonction de modification à venir');
                  }}
                >
                  ✏️ 
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVELLE OPPORTUNITÉ */}
      {showNewOppModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20
        }}>
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.07)'
            }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff' }}>
                  ✨ Nouvelle Opportunité
                </div>
                <div style={{ fontSize: 11, color: '#667', marginTop: 4 }}>
                  Créer une nouvelle opportunité commerciale
                </div>
              </div>
              <button
                onClick={() => setShowNewOppModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#667',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                
                {/* Nom du projet */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    NOM DU PROJET <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newOpp.name}
                    onChange={(e) => setNewOpp({...newOpp, name: e.target.value})}
                    placeholder="Ex: Site e-commerce pour XYZ Corp"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Client */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    CLIENT <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newOpp.client}
                    onChange={(e) => setNewOpp({...newOpp, client: e.target.value})}
                    placeholder="Nom de l'entreprise cliente"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    DESCRIPTION
                  </label>
                  <textarea
                    value={newOpp.description}
                    onChange={(e) => setNewOpp({...newOpp, description: e.target.value})}
                    placeholder="Description détaillée du projet..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Ligne 1: Filiale + Département */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      FILIALE <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <select
                      value={newOpp.filiale_id}
                      onChange={(e) => setNewOpp({...newOpp, filiale_id: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Sélectionner...</option>
                      {filiales.map(f => (
                        <option key={f.id} value={f.id}>{f.nom}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      DÉPARTEMENT
                    </label>
                    <select
                      value={newOpp.departement_id}
                      onChange={(e) => setNewOpp({...newOpp, departement_id: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Sélectionner...</option>
                      {departements.map(d => (
                        <option key={d.id} value={d.id}>{d.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ligne 2: Statut + Priorité */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      STATUT
                    </label>
                    <select
                      value={newOpp.status}
                      onChange={(e) => setNewOpp({...newOpp, status: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="planification">📋 Planification</option>
                      <option value="negociation">🤝 Négociation</option>
                      <option value="en_cours">🚀 En cours</option>
                      <option value="production">⚙️ Production</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      PRIORITÉ
                    </label>
                    <select
                      value={newOpp.priority}
                      onChange={(e) => setNewOpp({...newOpp, priority: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="basse">🟢 Basse</option>
                      <option value="normale">🟡 Normale</option>
                      <option value="haute">🟠 Haute</option>
                      <option value="urgente">🔴 Urgente</option>
                    </select>
                  </div>
                </div>

                {/* Ligne 3: Budget + Date début */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      BUDGET (FCFA)
                    </label>
                    <input
                      type="number"
                      value={newOpp.budget}
                      onChange={(e) => setNewOpp({...newOpp, budget: e.target.value})}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      DATE DE DÉBUT
                    </label>
                    <input
                      type="date"
                      value={newOpp.start_date}
                      onChange={(e) => setNewOpp({...newOpp, start_date: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              gap: 10,
              padding: '16px 24px',
              borderTop: '1px solid rgba(255,255,255,0.07)'
            }}>
              <button
                onClick={() => setShowNewOppModal(false)}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#888',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateOpp}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: loading ? '#888' : '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? '⏳ Création...' : '✨ Créer l\'opportunité'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVEAU PROSPECT */}
      {showNewProspectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20
        }}>
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.07)'
            }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff' }}>
                  🎯 Nouveau Prospect
                </div>
                <div style={{ fontSize: 11, color: '#667', marginTop: 4 }}>
                  Ajouter un nouveau contact commercial
                </div>
              </div>
              <button
                onClick={() => setShowNewProspectModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#667',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                
                {/* Nom entreprise */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    🏢 NOM DE L'ENTREPRISE <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newProspect.nom_entreprise}
                    onChange={(e) => setNewProspect({...newProspect, nom_entreprise: e.target.value})}
                    placeholder="Ex: ABC Corporation SARL"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Ligne 1: Secteur + Service */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      🏭 SECTEUR D'ACTIVITÉ <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <select
                      value={newProspect.secteur_activite}
                      onChange={(e) => setNewProspect({...newProspect, secteur_activite: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="Commerce">Commerce</option>
                      <option value="Secteur public">Secteur public</option>
                      <option value="Tourisme">Tourisme</option>
                      <option value="BTP">BTP</option>
                      <option value="Santé">Santé</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Transport">Transport</option>
                      <option value="Industrie">Industrie</option>
                      <option value="Services">Services</option>
                      <option value="Technologie">Technologie</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      🎯 FILIALE INTÉRESSÉE <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <select
                      value={newProspect.service_interesse}
                      onChange={(e) => setNewProspect({...newProspect, service_interesse: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Sélectionner...</option>
                      {filiales.map((filiale) => (
                        <option key={filiale.id} value={filiale.nom}>
                          {filiale.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ligne 2: Contact + Téléphone */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      👤 PERSONNE DE CONTACT
                    </label>
                    <input
                      type="text"
                      value={newProspect.contact_nom}
                      onChange={(e) => setNewProspect({...newProspect, contact_nom: e.target.value})}
                      placeholder="Ex: Jean Dupont"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      📞 TÉLÉPHONE
                    </label>
                    <input
                      type="tel"
                      value={newProspect.contact_tel}
                      onChange={(e) => setNewProspect({...newProspect, contact_tel: e.target.value})}
                      placeholder="+229 XX XX XX XX"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {/* Ligne 3: Potentiel + Température */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      💰 VALEUR POTENTIELLE (FCFA)
                    </label>
                    <input
                      type="number"
                      value={newProspect.valeur_potentielle}
                      onChange={(e) => setNewProspect({...newProspect, valeur_potentielle: e.target.value})}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      🌡️ NIVEAU D'INTÉRÊT
                    </label>
                    <select
                      value={newProspect.temperature}
                      onChange={(e) => setNewProspect({...newProspect, temperature: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="chaud">🔥 Chaud (Prêt à acheter)</option>
                      <option value="tiede">🟡 Tiède (Intéressé)</option>
                      <option value="froid">🔵 Froid (À qualifier)</option>
                    </select>
                  </div>
                </div>

                {/* Chargé de compte */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    👨‍💼 CHARGÉ DE COMPTE
                  </label>
                  <select
                    value={newProspect.responsable_id}
                    onChange={(e) => setNewProspect({...newProspect, responsable_id: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Auto-assigné (moi)</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.user_id}>
                        {emp.user?.name || `${emp.prenom} ${emp.nom}`}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              gap: 10,
              padding: '16px 24px',
              borderTop: '1px solid rgba(255,255,255,0.07)'
            }}>
              <button
                onClick={() => setShowNewProspectModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#888',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateProspect}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                🎯 Créer le prospect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVEAU DEVIS */}
      {showNewDevisModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20
        }}>
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.07)'
            }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff' }}>
                  📋 Nouveau Devis
                </div>
                <div style={{ fontSize: 11, color: '#667', marginTop: 4 }}>
                  Créer une nouvelle offre commerciale
                </div>
              </div>
              <button
                onClick={() => setShowNewDevisModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#667',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                
                {/* Client */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    🏢 CLIENT <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newDevis.client}
                    onChange={(e) => setNewDevis({...newDevis, client: e.target.value})}
                    placeholder="Ex: ABC Corporation SARL"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Service/Filiale */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    🎯 SERVICE/FILIALE <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <select
                    value={newDevis.service}
                    onChange={(e) => setNewDevis({...newDevis, service: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="FHC Technologie">FHC Technologie</option>
                    <option value="FHC Conseil">FHC Conseil</option>
                    <option value="FHC Formation">FHC Formation</option>
                    <option value="EMPLOI+">EMPLOI+</option>
                    <option value="FHC Location">FHC Location</option>
                  </select>
                </div>

                {/* Objet */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    📝 OBJET DU DEVIS <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <textarea
                    value={newDevis.objet}
                    onChange={(e) => setNewDevis({...newDevis, objet: e.target.value})}
                    placeholder="Description du devis..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Ligne 1: Montant + Durée validité */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      💰 MONTANT HT (FCFA) <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <input
                      type="number"
                      value={newDevis.montant}
                      onChange={(e) => setNewDevis({...newDevis, montant: e.target.value})}
                      placeholder="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      ⏱️ VALIDITÉ (JOURS)
                    </label>
                    <input
                      type="number"
                      value={newDevis.validite_jours}
                      onChange={(e) => setNewDevis({...newDevis, validite_jours: e.target.value})}
                      placeholder="30"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {/* Responsable */}
                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    👨‍💼 RESPONSABLE
                  </label>
                  <select
                    value={newDevis.resp}
                    onChange={(e) => setNewDevis({...newDevis, resp: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Auto-assigné</option>
                    {employees.map((emp, idx) => (
                      <option key={idx} value={emp.user?.name || emp.prenom + ' ' + emp.nom}>
                        {emp.user?.name || emp.prenom + ' ' + emp.nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Info - Numéro auto */}
                <div style={{
                  background: 'rgba(201,168,76,0.1)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <span style={{ fontSize: 18 }}>ℹ️</span>
                  <div style={{ fontSize: 11, color: '#C9A84C', lineHeight: 1.5 }}>
                    Le numéro de devis sera généré automatiquement au format <strong>DEV-{new Date().getFullYear()}-XXX</strong>
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              gap: 10,
              padding: '16px 24px',
              borderTop: '1px solid rgba(255,255,255,0.07)'
            }}>
              <button
                onClick={() => setShowNewDevisModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#888',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDevis}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: '#C9A84C',
                  border: 'none',
                  borderRadius: 8,
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                📋 Créer le devis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVELLE RELANCE */}
      {showNewRelanceModal && (
        <div style={{ 
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', 
          justifyContent: 'center', zIndex: 9999 
        }}>
          <div style={{ 
            background: '#162535', borderRadius: 12, padding: 24, width: '90%', 
            maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' 
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>📅 Planifier une relance</div>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Contact *</label>
              <input 
                value={newRelance.contact_nom}
                onChange={e => setNewRelance({...newRelance, contact_nom: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Objet *</label>
              <input 
                value={newRelance.objet}
                onChange={e => setNewRelance({...newRelance, objet: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Type *</label>
                <select 
                  value={newRelance.type}
                  onChange={e => setNewRelance({...newRelance, type: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                >
                  <option value="prospect">Prospect</option>
                  <option value="devis">Devis</option>
                  <option value="facture">Facture</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Date prévue *</label>
                <input 
                  type="date"
                  value={newRelance.date_prevue}
                  onChange={e => setNewRelance({...newRelance, date_prevue: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Canal</label>
                <select 
                  value={newRelance.canal}
                  onChange={e => setNewRelance({...newRelance, canal: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                >
                  <option value="email">Email</option>
                  <option value="telephone">Téléphone</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="visite">Visite</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Priorité</label>
                <select 
                  value={newRelance.priorite}
                  onChange={e => setNewRelance({...newRelance, priorite: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                >
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button 
                onClick={() => setShowNewRelanceModal(false)}
                style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #556', borderRadius: 6, color: '#aaa', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button 
                onClick={handleCreateRelance}
                disabled={loading}
                style={{ padding: '8px 16px', background: '#C9A84C', border: 'none', borderRadius: 6, color: '#1a1a0d', fontWeight: 600, cursor: 'pointer' }}
              >
                {loading ? 'Création...' : 'Planifier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVEAU APPRENANT */}
      {showNewApprenantModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 20
        }}>
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.07)'
            }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff' }}>
                  🎓 {editingApprenant ? 'Modifier un apprenant' : 'Ajouter un apprenant'}
                </div>
                <div style={{ fontSize: 11, color: '#667', marginTop: 4 }}>
                  Enregistrer un apprenant en formation
                </div>
              </div>
              <button
                onClick={() => setShowNewApprenantModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#667',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 4
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Prénom <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <input
                      value={newApprenant.prenom}
                      onChange={(e) => setNewApprenant({ ...newApprenant, prenom: e.target.value })}
                      placeholder="Ex: Ali"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Nom <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <input
                      value={newApprenant.nom}
                      onChange={(e) => setNewApprenant({ ...newApprenant, nom: e.target.value })}
                      placeholder="Ex: KOUASSI"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Formation <span style={{ color: '#ef8080' }}>*</span>
                  </label>
                  <input
                    value={newApprenant.formation}
                    onChange={(e) => setNewApprenant({ ...newApprenant, formation: e.target.value })}
                    placeholder="Ex: ADPME, Marketing Digital, Comptabilité"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Date de début <span style={{ color: '#ef8080' }}>*</span>
                    </label>
                    <input
                      type="date"
                      value={newApprenant.debut}
                      onChange={(e) => setNewApprenant({ ...newApprenant, debut: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Durée
                    </label>
                    <input
                      value={newApprenant.duree}
                      onChange={(e) => setNewApprenant({ ...newApprenant, duree: e.target.value })}
                      placeholder="Ex: 3 mois"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Montant à payer
                    </label>
                    <input
                      type="number"
                      value={newApprenant.montant_a_payer}
                      onChange={(e) => updateMontantField('montant_a_payer', e.target.value)}
                      placeholder="Ex: 150000"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Montant payé
                    </label>
                    <input
                      type="number"
                      value={newApprenant.montant_paye}
                      onChange={(e) => updateMontantField('montant_paye', e.target.value)}
                      placeholder="Ex: 50000"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Reste à payer
                    </label>
                    <input
                      type="number"
                      value={newApprenant.reste_a_payer}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Statut du paiement
                    </label>
                    <select
                      value={newApprenant.statut_paiement}
                      onChange={(e) => setNewApprenant({ ...newApprenant, statut_paiement: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="non_paye">Non payé</option>
                      <option value="partiel">Partiel</option>
                      <option value="paye">Payé</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Statut
                    </label>
                    <select
                      value={newApprenant.statut}
                      onChange={(e) => setNewApprenant({ ...newApprenant, statut: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="en_cours">En cours</option>
                      <option value="termine">Terminée</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Responsable
                    </label>
                    <select
                      value={newApprenant.responsable_id}
                      onChange={(e) => setNewApprenant({ ...newApprenant, responsable_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#0d1b2a',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Non assigné</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Commentaire
                  </label>
                  <textarea
                    value={newApprenant.commentaire}
                    onChange={(e) => setNewApprenant({ ...newApprenant, commentaire: e.target.value })}
                    rows={3}
                    placeholder="Notes sur l'apprenant ou sur la formation"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <button
                onClick={() => setShowNewApprenantModal(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#888',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSaveApprenant}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: '#ab47bc',
                  border: 'none',
                  borderRadius: 8,
                  color: '#1a1a0d',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                🎓 Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVELLE ACTIVITÉ */}
      {showNewActiviteModal && (
        <div style={{ 
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', 
          justifyContent: 'center', zIndex: 9999 
        }}>
          <div style={{ 
            background: '#162535', borderRadius: 12, padding: 24, width: '90%', 
            maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' 
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>📝 Enregistrer une activité</div>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Contact *</label>
              <input 
                value={newActivite.contact_nom}
                onChange={e => setNewActivite({...newActivite, contact_nom: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Objet *</label>
              <input 
                value={newActivite.objet}
                onChange={e => setNewActivite({...newActivite, objet: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Type *</label>
                <select 
                  value={newActivite.type}
                  onChange={e => setNewActivite({...newActivite, type: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                >
                  <option value="appel">Appel téléphonique</option>
                  <option value="email">Email</option>
                  <option value="reunion">Réunion</option>
                  <option value="visite">Visite client</option>
                  <option value="demo">Démonstration</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Date & Heure *</label>
                <input 
                  type="datetime-local"
                  value={newActivite.date_activite}
                  onChange={e => setNewActivite({...newActivite, date_activite: e.target.value})}
                  style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Résultat</label>
              <textarea 
                value={newActivite.resultat}
                onChange={e => setNewActivite({...newActivite, resultat: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, minHeight: 60, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#667', display: 'block', marginBottom: 5 }}>Action suivante</label>
              <input 
                value={newActivite.action_suivante}
                onChange={e => setNewActivite({...newActivite, action_suivante: e.target.value})}
                style={{ width: '100%', background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13 }}
                placeholder="Ex: Envoyer devis, Relance dans 1 semaine..."
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button 
                onClick={() => setShowNewActiviteModal(false)}
                style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #556', borderRadius: 6, color: '#aaa', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button 
                onClick={handleCreateActivite}
                disabled={loading}
                style={{ padding: '8px 16px', background: '#C9A84C', border: 'none', borderRadius: 6, color: '#1a1a0d', fontWeight: 600, cursor: 'pointer' }}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CRMModule;
